/*
 * One glyph per navigation item — 26 Aug 2026, for the docked sidebar.
 *
 * ── Why these live here and not in the design system ────────────────────────────────────────────
 *
 * An icon is not a shape, it is a **claim about what a screen is**, and only this app knows that
 * `standup` is a huddle and `my-pipeline` is a board. `Nav` takes the glyph as an opaque node for
 * exactly the reason it takes the link as one: the package owns structure and states, the app owns
 * navigation and its meanings. A shared icon set in the design system would either be a dumping
 * ground of product-specific pictures or a generic library nothing here matched.
 *
 * ── Why they are drawn rather than typed ────────────────────────────────────────────────────────
 *
 * The same reason `CrossGlyph` is drawn: a character sits differently in every face, changes with
 * a font fallback, and is read aloud by a screen reader. Every one of these is `aria-hidden`, and
 * the accessible name is always the label beside it — visible when the rail is open, and
 * visually hidden when it is docked. **A docked rail must never be a rail of unnamed pictures.**
 *
 * ── Why an icon per item is a requirement of docking, not a decoration ──────────────────────────
 *
 * A sidebar that collapses to nothing gives the width back and takes navigation away; a sidebar
 * that collapses to labels cannot get below the longest label. The icon rail is what makes 3.25rem
 * possible while every destination stays one click away, and it is what every tool that docks a
 * panel on the left does.
 *
 * They share one geometry on purpose — a 20-unit box, a 1.5 stroke, round joins, no fills — so
 * eleven glyphs read as one set rather than as eleven pictures. An icon that is heavier than its
 * neighbours reads as selected.
 */

import type { ReactNode } from 'react';

function Glyph({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 20 20"
      className="tf-nav__icon"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

/**
 * Keyed by route id, which is the manifest's own identifier — so an item whose glyph is missing
 * renders without one rather than with the wrong one, and a renamed route loses its icon loudly at
 * the next glance instead of silently inheriting a neighbour's.
 */
export const NAV_ICONS: Record<string, ReactNode> = {
  home: (
    <Glyph>
      <path d="M3 8.6 10 3.2l7 5.4V16a1 1 0 0 1-1 1h-3.5v-4.6h-5V17H4a1 1 0 0 1-1-1z" />
    </Glyph>
  ),
  // A client is a company: the building, not the person we speak to there.
  clients: (
    <Glyph>
      <path d="M3.5 17V4.2a1 1 0 0 1 1-1h6.5a1 1 0 0 1 1 1V17" />
      <path d="M12 8.2h3.5a1 1 0 0 1 1 1V17" />
      <path d="M2.5 17h15M6 6.5h3M6 9.5h3M6 12.5h3" />
    </Glyph>
  ),
  jobs: (
    <Glyph>
      <rect x="2.8" y="6" width="14.4" height="10.5" rx="1.6" />
      <path d="M7.4 6V4.6A1.6 1.6 0 0 1 9 3h2a1.6 1.6 0 0 1 1.6 1.6V6" />
      <path d="M2.8 10.2h14.4" />
    </Glyph>
  ),
  // The board, seen from above — three columns of unequal height, which is what a pipeline is.
  'my-pipeline': (
    <Glyph>
      <rect x="3" y="3.5" width="3.8" height="13" rx="1.2" />
      <rect x="8.1" y="3.5" width="3.8" height="8.5" rx="1.2" />
      <rect x="13.2" y="3.5" width="3.8" height="10.7" rx="1.2" />
    </Glyph>
  ),
  // A person on a record, rather than a bare person: this is the file, not the human.
  candidates: (
    <Glyph>
      <rect x="2.6" y="4.2" width="14.8" height="11.6" rx="2" />
      <circle cx="7.4" cy="9" r="1.9" />
      <path d="M4.6 14a2.9 2.9 0 0 1 5.6 0" />
      <path d="M12.6 8.6h3.2M12.6 11.6h3.2" />
    </Glyph>
  ),
  interviews: (
    <Glyph>
      <rect x="3" y="4.6" width="14" height="12.4" rx="1.6" />
      <path d="M3 8.8h14M7 3v3.2M13 3v3.2" />
    </Glyph>
  ),
  // A document that has been agreed to — the tick is the whole difference from a submission.
  offers: (
    <Glyph>
      <path d="M5 3h5.8L15.4 7.2V16a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M10.6 3.2v4h4.6" />
      <path d="M6.8 12.4 8.6 14.2l3.4-3.6" />
    </Glyph>
  ),
  insights: (
    <Glyph>
      <path d="M3 16.6h14" />
      <path d="M5.8 16.6v-4.4M10 16.6V5.6M14.2 16.6v-7.2" />
    </Glyph>
  ),
  // Two voices, because a huddle is people talking and not a report being read.
  standup: (
    <Glyph>
      <path d="M3 11.4V5.8a1.8 1.8 0 0 1 1.8-1.8h6.4A1.8 1.8 0 0 1 13 5.8v2.8a1.8 1.8 0 0 1-1.8 1.8H5.8z" />
      <path d="M8.2 12.6a1.8 1.8 0 0 0 1.8 1.8h3.6L16.8 17v-5.6a1.8 1.8 0 0 0-1.8-1.8h-1" />
    </Glyph>
  ),
  teams: (
    <Glyph>
      <circle cx="7.8" cy="7" r="2.6" />
      <path d="M3 16.4a4.8 4.8 0 0 1 9.6 0" />
      <path d="M13.4 5.2a2.6 2.6 0 0 1 0 5.1" />
      <path d="M14.2 16.4a5.6 5.6 0 0 0-1.2-3.4" />
    </Glyph>
  ),
  // Sliders rather than a cog: these are settings somebody adjusts, not a machine they service.
  settings: (
    <Glyph>
      <path d="M3 6.4h8.2M15.6 6.4H17" />
      <circle cx="13.4" cy="6.4" r="2.2" />
      <path d="M3 13.6h3.6M10.8 13.6H17" />
      <circle cx="8.7" cy="13.6" r="2.2" />
    </Glyph>
  ),
};
